import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
} from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { Experiment, Experiments } from '../adobe-target-server-side/types'
import { setSsrExperiments } from '../helpers/ssr-experiments-store'

interface SsrExperimentsProviderProps {
  experiments: Experiments
  children?: React.ReactNode
}

interface SsrExperimentsContextTypes {
  getExperienceIdForExperiment: (experimentName: string) => number
}

export const SsrExperimentsContext = createContext<
  SsrExperimentsContextTypes | undefined
>({
  getExperienceIdForExperiment: () => DEFAULT_EXPERIMENT_NOT_FOUND_ID,
})

const DEFAULT_EXPERIMENT_NOT_FOUND_ID = 0

export const getExperiment = (
  experiments: Experiments,
  experimentName: string,
) =>
  experiments.find(
    (experiment: Experiment) => experiment.name === experimentName,
  )

export const getExperienceId = (experiment: Experiment | undefined) =>
  experiment ? Number(experiment.experienceId) : DEFAULT_EXPERIMENT_NOT_FOUND_ID

export const SsrExperimentsProvider = ({
  experiments,
  children,
}: SsrExperimentsProviderProps) => {
  // Update store in useLayoutEffect to run AFTER children have rendered
  // This ensures BAU entries created by hooks during render are preserved
  // On SSR, useLayoutEffect doesn't run, but that's okay because analytics won't fire until client
  useLayoutEffect(() => {
    setSsrExperiments(experiments)
  }, [experiments])

  // Also call during render for SSR to ensure store is populated on server
  if (typeof window === 'undefined') {
    setSsrExperiments(experiments)
  }

  const getExperienceIdForExperiment = useCallback(
    (experimentName: string) => {
      const experiment = getExperiment(experiments, experimentName)
      // only record experiments that exist in Datadog to make the feature flag view clearer
      if (experiment?.experienceId !== undefined) {
        // replace hyphens with underscores to make querying work correctly in Datadog
        const datadogExperimentName = `experiment_${experimentName.replace(
          /-/g,
          '_',
        )}`
        const datadogExperienceId =
          experiment.experienceId === 0
            ? 'Control'
            : `Variant ${experiment.experienceId}`

        datadogRum.addFeatureFlagEvaluation(
          datadogExperimentName,
          datadogExperienceId,
        )
      }
      return getExperienceId(experiment)
    },
    [experiments],
  )

  return (
    <SsrExperimentsContext.Provider value={{ getExperienceIdForExperiment }}>
      {children}
    </SsrExperimentsContext.Provider>
  )
}

export const useSsrExperiments = (): SsrExperimentsContextTypes => {
  const context = useContext(SsrExperimentsContext)

  if (!context) {
    throw new Error(
      'useSsrExperiments must be used within a SsrExperimentProvider',
    )
  }

  return context
}
